import { ReactNode } from "react";
import {
  Box,
  Paper,
  Text,
  Stack,
  Group,
  ThemeIcon,
  Badge,
  useComputedColorScheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconFileText,
  IconCircleCheck,
  IconRosette,
  IconHome2,
  IconUser,
  IconShieldCheck,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Illustrated, literal lifecycle storyboard — the rule-library journey shown
 * with recognizable objects instead of an abstract gem:
 *
 *   State rulebook  →  Group home(s) + residents  →  Survey passed
 *
 * Driven by the `stage` prop from JewelSection (Author → Publish → Adopt →
 * Enforce → Prove). Steps the lifecycle hasn't reached yet sit ghosted in
 * place and light up as it advances, dimming again when it loops, so the whole
 * story plays on repeat without the layout ever moving.
 *
 * Two layouts, same content: a wide horizontal storyboard, and below `WIDE` a
 * compact vertical one — slim icon-left rows joined by short vertical
 * connectors. The scene used to simply wrap, which turned the three cards into
 * a 524px column with the connectors stranded off to one side.
 */

const GREEN = "#12b886";
const TEAL = "#0ca678";

/**
 * The horizontal storyboard needs 784px of inner width (230 + 240 + 170 cards,
 * two 56px connectors, four 8px gaps) and measures as a single row from a
 * 848px viewport up. 56em keeps a cushion above that.
 */
const WIDE = "(min-width: 56em)";

/**
 * Not-yet-reached steps are ghosted, not hidden.
 *
 * Their space has to stay reserved either way — mounting a card mid-cycle
 * changed the scene's height and reflowed every section below the fold. But
 * reserving it while rendering nothing left a card-shaped hole until the loop
 * caught up. Ghosting fills that space with the step itself, and matches how
 * the stage rail above already renders stages the lifecycle hasn't reached.
 */
const ghost = (reached: boolean) => ({
  opacity: reached ? 1 : 0.28,
  scale: reached ? 1 : 0.97,
});

const REQUIREMENTS = [
  "Annual physician review",
  "Medication administration log",
  "Individual service plan",
  "Fire & safety drill record",
];

/** Animated connector that fills once its stage is reached — left→right on the
 *  horizontal storyboard, top→bottom when the scene is stacked. */
function Flow({
  active,
  color = GREEN,
  vertical = false,
}: {
  active: boolean;
  color?: string;
  vertical?: boolean;
}) {
  return (
    <Box
      style={{
        flex: vertical ? "0 0 20px" : "0 0 56px",
        width: vertical ? 4 : undefined,
        height: vertical ? 20 : 4,
        borderRadius: 2,
        background: "rgba(120,120,120,0.18)",
        position: "relative",
        overflow: "hidden",
        alignSelf: "center",
      }}
    >
      <motion.div
        initial={false}
        animate={
          vertical
            ? { height: active ? "100%" : "0%" }
            : { width: active ? "100%" : "0%" }
        }
        transition={{ duration: 0.6, ease: "easeInOut" }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          ...(vertical ? { right: 0 } : { bottom: 0 }),
          borderRadius: 2,
          background: `linear-gradient(${vertical ? 180 : 90}deg, ${color}, ${TEAL})`,
        }}
      />
    </Box>
  );
}

/**
 * Card interior: a centered column on the wide storyboard, a slim icon-left row
 * when stacked. Keeping one component for both is what lets the compact layout
 * carry the same content instead of dropping detail to fit.
 */
function CardBody({
  wide,
  icon,
  children,
}: {
  wide: boolean;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: wide ? "column" : "row",
        alignItems: "center",
        gap: wide ? 0 : 14,
        textAlign: wide ? "center" : "left",
      }}
    >
      {icon}
      <Box style={{ flex: wide ? undefined : 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

export interface LifecycleSceneProps {
  /** Current lifecycle stage (0–4). */
  stage: number;
}

export function LifecycleScene({ stage }: LifecycleSceneProps) {
  const dark = useComputedColorScheme("light") === "dark";
  const cardBg = dark ? "#1f2421" : "#ffffff";
  const wide = useMediaQuery(WIDE, false, { getInitialValueInEffect: false });

  const published = stage >= 1;
  const adopted = stage >= 2;
  const enforced = stage >= 3;
  const proven = stage >= 4;

  /** Stacked cards fill the column; the storyboard sizes each slot to its art. */
  const slot = (fixed: number) => (wide ? fixed : "100%");

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: wide ? "row" : "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        // Never wrap: wrapping is what produced the ragged column this compact
        // layout replaces.
        flexWrap: "nowrap",
        minHeight: wide ? 320 : undefined,
        // Keep the stacked column from stretching across a tablet.
        maxWidth: wide ? undefined : 420,
        margin: wide ? undefined : "0 auto",
        padding: "1rem 0",
      }}
    >
      {/* ── 1. STATE RULEBOOK ─────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: wide ? undefined : "100%" }}
      >
        <Paper
          radius="lg"
          p={wide ? "md" : "sm"}
          shadow="md"
          style={{
            width: slot(230),
            background: cardBg,
            border: `1px solid ${adopted ? `${GREEN}55` : "rgba(120,120,120,0.18)"}`,
            position: "relative",
          }}
        >
          {/* Published seal */}
          <AnimatePresence>
            {published && (
              <motion.div
                initial={{ scale: 0, rotate: -25, opacity: 0 }}
                animate={{ scale: 1, rotate: -12, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
                style={{
                  position: "absolute",
                  top: -14,
                  right: -10,
                  zIndex: 2,
                }}
              >
                <Badge
                  size="lg"
                  radius="sm"
                  color="accentGreen"
                  variant="filled"
                  leftSection={<IconRosette size={14} />}
                >
                  PUBLISHED v1
                </Badge>
              </motion.div>
            )}
          </AnimatePresence>

          <Group gap="xs" mb="sm" wrap="nowrap">
            <ThemeIcon
              size={36}
              radius="md"
              variant="light"
              color="accentGreen"
            >
              <IconFileText size={20} />
            </ThemeIcon>
            <Box>
              <Text size="xs" c="dimmed" fw={600}>
                STATE RULE SET
              </Text>
              <Text size="sm" fw={700}>
                Illinois · CILA
              </Text>
            </Box>
          </Group>

          <Stack gap={6}>
            {REQUIREMENTS.map((req, i) => (
              <motion.div
                key={req}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 + i * 0.12, duration: 0.3 }}
              >
                <Group gap={6} wrap="nowrap">
                  <IconCircleCheck size={15} color={GREEN} />
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {req}
                  </Text>
                </Group>
              </motion.div>
            ))}
          </Stack>
        </Paper>
      </motion.div>

      <Flow active={adopted} vertical={!wide} />

      {/* ── 2. GROUP HOME(S) + RESIDENTS ──────────────────────────────── */}
      <Box style={{ width: slot(240), position: "relative" }}>
        <motion.div
          initial={false}
          animate={ghost(adopted)}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
        >
          {/* Satellite homes (replication across facilities). They fan out from
              the card's centre, which only reads on the wide storyboard — when
              stacked, "+ 2 more facilities" carries the same point. */}
          {wide && (
            <AnimatePresence>
              {enforced && (
                <>
                  {[-1, 1].map((dir) => (
                    <motion.div
                      key={dir}
                      initial={{ opacity: 0, x: 0, scale: 0.6 }}
                      animate={{ opacity: 0.6, x: dir * 70, scale: 0.7 }}
                      exit={{ opacity: 0, x: 0 }}
                      transition={{ duration: 0.5 }}
                      style={{
                        position: "absolute",
                        top: 8,
                        left: "50%",
                        marginLeft: -28,
                        zIndex: 0,
                      }}
                    >
                      <ThemeIcon
                        size={56}
                        radius="md"
                        variant="light"
                        color="healthyGreen"
                      >
                        <IconHome2 size={30} />
                      </ThemeIcon>
                    </motion.div>
                  ))}
                </>
              )}
            </AnimatePresence>
          )}

          <Paper
            radius="lg"
            p={wide ? "md" : "sm"}
            shadow="md"
            style={{
              background: cardBg,
              border: `1px solid ${GREEN}55`,
              position: "relative",
              zIndex: 1,
            }}
          >
            <CardBody
              wide={wide}
              icon={
                <ThemeIcon
                  size={56}
                  radius="md"
                  variant="filled"
                  color="healthyGreen"
                  style={{ flexShrink: 0 }}
                >
                  <IconHome2 size={30} />
                </ThemeIcon>
              }
            >
              <Text size="sm" fw={700} mt={wide ? 6 : 0}>
                Maple Group Home
              </Text>
              <Text size="xs" c="dimmed">
                {enforced ? "+ 2 more facilities" : "Adopting rule set…"}
              </Text>

              {/* Residents each inheriting the requirements */}
              <Group gap={6} justify={wide ? "center" : "flex-start"} mt="xs">
                {[0, 1, 2, 3].map((i) => (
                  <Box key={i} style={{ position: "relative" }}>
                    <ThemeIcon
                      size={30}
                      radius="xl"
                      variant="light"
                      color="gray"
                    >
                      <IconUser size={16} />
                    </ThemeIcon>
                    <AnimatePresence>
                      {enforced && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ delay: i * 0.1, duration: 0.3 }}
                          style={{
                            position: "absolute",
                            bottom: -3,
                            right: -3,
                          }}
                        >
                          <IconCircleCheck
                            size={15}
                            color="#fff"
                            fill={GREEN}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>
                ))}
              </Group>
            </CardBody>
          </Paper>
        </motion.div>
      </Box>

      <Flow active={proven} color={TEAL} vertical={!wide} />

      {/* ── 3. SURVEY PASSED ──────────────────────────────────────────── */}
      <Box style={{ width: slot(170) }}>
        <motion.div
          initial={false}
          animate={ghost(proven)}
          transition={{ type: "spring", stiffness: 260, damping: 18 }}
        >
          <Paper
            radius="lg"
            p={wide ? "md" : "sm"}
            shadow="md"
            style={{
              background: `linear-gradient(135deg, ${GREEN}, ${TEAL})`,
            }}
          >
            <CardBody
              wide={wide}
              icon={
                <ThemeIcon
                  size={56}
                  radius="xl"
                  variant="white"
                  style={{ color: GREEN, flexShrink: 0 }}
                >
                  <IconShieldCheck size={32} />
                </ThemeIcon>
              }
            >
              <Text size="sm" fw={800} c="white" mt={wide ? 8 : 0}>
                Survey Passed
              </Text>
              <Text size="xs" c="white" opacity={0.9}>
                Audit-ready, any date
              </Text>
            </CardBody>
          </Paper>
        </motion.div>
      </Box>
    </Box>
  );
}
